import { html, TemplateResult } from 'lit'
import { keyed } from 'lit/directives/keyed.js'
import './tile-part-story-host.js'

const baseProduct = {
  id: 26,
  brand: { name: 'CloudSuite', picture: 'src/testing/assets/svg/logo.svg' },
  code: '10001',
  // One levy — the baseline, between withConfigurator's zero and withVariants' two.
  levies: [
    { name: 'Waste management contribution', amount: '€ 100,00', amount_no_format: 100, description: 'Waste management contribution' },
  ],
  // All 4 renditions filled in — the canvas always previews the 'small' rendition specifically
  // (see base-product-tile/CLAUDE.md), so leaving it empty (as this used to) silently shows no
  // image at all regardless of what 'normal' is set to.
  default_picture: {
    large: 'src/testing/assets/images/product/default_image_large.png',
    mini: 'src/testing/assets/images/product/default_image_mini.png',
    normal: 'src/testing/assets/images/product/default_image_medium.png',
    small: 'src/testing/assets/images/product/default_image_small.png',
  },
  name: 'Dummy product',
  ordering: {
    cannot_be_added_to_basket: false,
    default_uom: 'Unit',
    maximum_order_quantity: null,
    minimum_order_quantity: null,
    order_quantity: 1,
  },
  page_link: '/en_US/p/eetkamerstoel-houten-poot-rond/22/',
  picture: {
    large: 'src/testing/assets/images/product/default_image_large.png',
    mini: 'src/testing/assets/images/product/default_image_mini.png',
    normal: 'src/testing/assets/images/product/default_image_medium.png',
    small: 'src/testing/assets/images/product/default_image_small.png',
  },
  prices: {
    consumer: '200.0000',
    currency_code: 'EUR',
    customer: '180.0000',
    sale: '125.00',
    sale_taxed: '151.25',
    list: '200.00',
    display_as_discount: true,
  },
  stock: {
    amount: 10,
    message: 'In stock',
    alt_msg: '',
  },
  rating: { main: { value: 4, count: 128 } },
  tags: [{
    as_badge: true,
    badge_type: 'info',
    name: 'New arrival',
    picture: '',
  },
  {
    as_badge: true,
    badge_type: 'success',
    name: 'Top rated',
    picture: '',
  }],
  num_variants: 0,
  variants: [],
  // One of each display_mode ('icon'/'text'/'icon_text') — the real per-attribute field the
  // backend returns (see FeaturedAttributes in search-results.ts), so csc-featured-attributes'
  // own variant filter (which matches against this key) has a realistic mix to filter against.
  featured_attributes: [
    { value: 'Eco friendly', picture: 'leaf', display_mode: 'icon' },
    { value: 'Premium quality', picture: '', display_mode: 'text' },
    { value: 'Fast delivery', picture: 'truck', display_mode: 'icon_text' },
  ],
}

const withVariantsProduct = {
  ...baseProduct,
  brand: { name: 'Solace Home', picture: 'src/testing/assets/svg/logo.svg' },
  code: '10002',
  name: 'Product with variants',
  rating: { main: { value: 4.5, count: 62 } },
  tags: [{
    as_badge: true,
    badge_type: 'sale',
    name: 'Sale',
    picture: 'src/testing/assets/images/badge.png',
  },
  {
    as_badge: true,
    badge_type: 'bestseller',
    name: 'Bestseller',
    picture: '',
  }],
  // Two levies — showcases the list rendering (multiple cse-list-items), unlike the other two
  // presets which each have exactly one or zero.
  levies: [
    { name: 'Waste management contribution', amount: '€ 100,00', amount_no_format: 100, description: 'Waste management contribution' },
    { name: 'Environmental recycling fee', amount: '€ 25,00', amount_no_format: 25, description: 'Environmental recycling fee' },
  ],
  num_variants: 4,
  // `variant` (singular) is the currently-selected variant's own grouping
  // info — this is what the variant_grouping condition checks (see
  // isPartOfVariantGroup in variant-grouping.ts), separate from the plural
  // `variants` list below, which only feeds the swatch-list component.
  variant: {
    id: 18659,
    name: 'Product with variants',
    type: 'group_by_attributes',
    attributes: [
      { id: 150, 
        name: 'Color group', 
        value: 'Brown', 
        value_id: 6635, 
        picture: null, 
        css_color: '#964B00', 
        display_mode: 'property_color' 
      },
    ],
  },
  variants: [
    {
      page_link: '/en-nl/p/eetkamerstoel-kunststof-poot-rond-bruin/26/',
      variant: { attributes: [{ name: 'Color group', value: 'Brown', picture: '', css_color: '#964B00', display_mode: 'property_color' }] },
    },
    {
      page_link: '/en-nl/p/eetkamerstoel-metalen-poot-vierkant/25/',
      variant: { attributes: [{ name: 'Color group', value: 'Black', picture: '', css_color: '#000000', display_mode: 'property_color' }] },
    },
    {
      page_link: '/en-nl/p/eetkamerstoel-rood/27/',
      variant: { attributes: [{ name: 'Color group', value: 'Red', picture: '', css_color: '#FF0000', display_mode: 'property_color' }] },
    },
    {
      page_link: '/en-nl/p/eetkamerstoel-grijs/28/',
      variant: { attributes: [{ name: 'Color group', value: 'Grey', picture: '', css_color: '#808080', display_mode: 'property_color' }] },
    },
  ],
  stock: {
    amount: 3,
    message: 'Low stock',
    alt_msg: 'Only a few items left',
    indication: {
      rule_id: 1,
      style: 'warning',
      icon: 'triangle-exclamation',
      text: 'Only 3 left',
    },
  },
  featured_attributes: [
    { value: 'Multiple colors', picture: 'palette', display_mode: 'icon_text' },
    { value: 'Water resistant', picture: 'droplet', display_mode: 'icon' },
  ],
}

export type TilePartProductPreset =
  | 'default'
  | 'withVariants'
  | 'withNonGroupedVariants'
  | 'withConfigurator'
  | 'notOrderable'

export const tilePartProductPresets: Record<TilePartProductPreset, object> = {
  default: baseProduct,
  withVariants: withVariantsProduct,
  withConfigurator: {
    ...baseProduct,
    brand: { name: 'Modulaire Works' },
    code: '10003',
    name: 'Configurable product',
    // Deliberately no rating data — demonstrates csc-product-rating falling back to a real
    // "no reviews yet" 0/(0) state, unlike the other two presets.
    rating: {},
    tags: [{
      as_badge: true,
      badge_type: 'default',
      name: 'Configurable',
      picture: '',
    }],
    configurator: { id: 1, url: '/en_US/configurator/1/' },
    // Deliberately no levies at all — demonstrates csc-levies rendering nothing, unlike the
    // other two presets (one, two).
    levies: [],
    num_variants: 0,
    variants: [],
    featured_attributes: [
      { value: 'Fully customizable', picture: 'sliders', display_mode: 'icon_text' },
      { value: 'Made to order', picture: '', display_mode: 'text' },
    ],
  },
  // Variants that aren't grouped (no variant type): has_variants is true, variant_grouping is false.
  withNonGroupedVariants: {
    ...withVariantsProduct,
    code: '10004',
    name: 'Product with loose variants',
    variant: { ...withVariantsProduct.variant, type: null },
  },
  // Can't be added to the basket: the orderable condition is false.
  notOrderable: {
    ...baseProduct,
    code: '10005',
    name: 'Product that cannot be ordered',
    ordering: { ...baseProduct.ordering, cannot_be_added_to_basket: true },
    // No badge tags either — previews the Tags widget rendering nothing.
    tags: [],
  },
}

export const TILE_PART_PRODUCT_PRESETS = Object.keys(tilePartProductPresets) as TilePartProductPreset[]

export interface TilePartInjectorArgs {
  locale?: string
  productPreset?: TilePartProductPreset
}

/**
 * Wrapped in `keyed` so switching `productPreset` always remounts the tile part from a fresh
 * connect — the product-tile-part components consume product data through Lit context without
 * `subscribe: true`, so an in-place context update alone would not re-render them.
 */
export function wrapTilePart(inner: TemplateResult, args: TilePartInjectorArgs): TemplateResult {
  const preset = args.productPreset ?? 'default'
  const product = tilePartProductPresets[preset]

  return html`${keyed(preset, html`
    <story-tile-part-host product=${JSON.stringify(product)}>
      ${inner}
    </story-tile-part-host>
  `)}`
}
